import React, { useState, useEffect, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { Link, useParams, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { URLS, SAGA_ACTIONS, handleApiCall, DATE_FORMAT, API_URL, STORAGE } from "src/_config";
import PermissionList from "./PermissionList";
import InviteMemberModal from "./InviteMemberModal";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { ReactSearchAutocomplete } from "react-search-autocomplete";
import { useUsersManagementApi } from "src/_common/hooks/actions/usersManagement/appUsersManagementApiHook";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { useEventApi } from "src/_common/hooks/actions/event/appEventApiHook";
import { useTranslation } from "src/_common/hooks/useTranslation";
import DateInput from "../../_common/components/form-elements/datepicker/dateInput";
import moment from "moment";
import { useRolesApi } from "src/_common/hooks/actions";

interface AddMemberFormData {
  name: string;
  email: string;
  mobileNumber: string;
  countryCode: string;
  designation: string;
  dateOfBirth: Date | undefined;
  gender: string;
  employeeCode: string;
  homeTown: string;
  language: string;
  maritalStatus: string;
  nationality: string;
  religion: string;
  permanentAddress: string;
  currentAddress: string;
  password: string;
  confirmPassword: string;
  assignWorkspace: string;
  assignRole: number;
}

const AddMemberForm: React.FC = () => {
  const { memberId } = useParams<{ memberId?: string }>();
  const isEditMode = Boolean(memberId);
  const [activeTab, setActiveTab] = useState<"info" | "permission">("info");
  const [profilePhoto, setProfilePhoto] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string | null>(null);

  // Designation autocomplete states
  const [showDesignationDropdown, setShowDesignationDropdown] = useState(false);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [designations, setDesignations] = useState<Array<{ id: string; name: string }>>([]);
  const [filteredDesignations, setFilteredDesignations] = useState<Array<{ id: string; name: string }>>([]);
  const [isLoadingDesignations, setIsLoadingDesignations] = useState<boolean>(false);
  const [designationSearchValue, setDesignationSearchValue] = useState("");
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [defaultCountry, setDefaultCountry] = useState<string>("us");
  const [phoneCode, setPhoneCode] = useState<string>(""); // State to track phone country code

  const dispatch = useDispatch();
  const history = useHistory();
  const usersManagementApi = useUsersManagementApi();
  const toast = useToaster();
  const eventApi = useEventApi();
  const { t } = useTranslation("member");

  // Refs
  const designationRef = useRef<HTMLDivElement>(null);
  const designationInputRef = useRef<HTMLDivElement>(null);

  // Redux selector for workplaces
  const { workplaces } = useSelector((state: any) => ({
    workplaces: state.global.workplaces,
  }));

  const workplacesData = workplaces && workplaces.length > 0 ? workplaces : [];
  const { callGetRoles } = useRolesApi();
  const [roles, setRoles] = useState<any[]>([]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<AddMemberFormData>({
    defaultValues: {
      name: "",
      email: "",
      mobileNumber: "",
      countryCode: "+1",
      designation: "",
      dateOfBirth: undefined,
      gender: "",
      employeeCode: "",
      homeTown: "",
      language: "",
      maritalStatus: "",
      nationality: "",
      religion: "",
      permanentAddress: "",
      currentAddress: "",
      password: "",
      confirmPassword: "",
      assignWorkspace: "",
      assignRole: 1,
    },
  });

  // Fetch workplaces data on component mount
  useEffect(() => {
    dispatch({
      type: SAGA_ACTIONS.COMMON.WORK_PLACES,
      payload: { limit: 30, offset: 0 },
    });

    // Load designations
    loadDesignations();

    if (!isEditMode) {
      loadRoles();
    }

    // Detect country from user's geolocation
    fetch("https://ipapi.co/json/")
      .then((response) => response.json())
      .then((data) => {
        if (data.country_code) {
          setDefaultCountry(data.country_code.toLowerCase());
        }
      })
      .catch(() => {
        // Fallback to US if detection fails
        setDefaultCountry("us");
      });

    // If in edit mode, load member data
    if (isEditMode && memberId && activeTab === "info") {
      loadMemberData(memberId);
    }
  }, [dispatch, isEditMode, memberId, activeTab]);

  // Handle click outside for designation dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (designationRef.current && !designationRef.current.contains(event.target as Node)) {
        setShowDesignationDropdown(false);
        setShowAutocomplete(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const loadDesignations = (searchName: string = "") => {
    setIsLoadingDesignations(true);
    const params: any = { limit: 100 };
    if (searchName.trim()) {
      params.name = searchName.trim();
    }
    handleApiCall(
      eventApi.callGetDesignations,
      params,
      (resp: any) => {
        if (resp?.designations) {
          const formattedDesignations = resp.designations.map((designation: any) => ({
            id: designation.id,
            name: designation.name,
          }));
          setDesignations(formattedDesignations);
          setFilteredDesignations(formattedDesignations);
        } else if (resp && Array.isArray(resp)) {
          const formattedDesignations = resp.map((designation: any) => ({
            id: designation.id,
            name: designation.name,
          }));
          setDesignations(formattedDesignations);
          setFilteredDesignations(formattedDesignations);
        } else if (resp?.data && Array.isArray(resp.data)) {
          const formattedDesignations = resp.data.map((designation: any) => ({
            id: designation.id || designation.designation_id,
            name: designation.name || designation.designation_name,
          }));
          setDesignations(formattedDesignations);
          setFilteredDesignations(formattedDesignations);
        }
        setIsLoadingDesignations(false);
      },
      toast,
      false,
      true
    );
  };

  const handleDesignationSearch = (searchValue: string) => {
    if (searchValue.length > 1) {
      loadDesignations(searchValue);
    } else {
      loadDesignations(); // Load all designations when search is cleared
    }
  };

  const handleDesignationInputChange = (value: string, onChange: Function) => {
    onChange(value);
    setDesignationSearchValue(value);
    setShowDesignationDropdown(false); // Close example dropdown when typing

    if (value.length > 0) {
      const filtered = designations.filter((designation) =>
        designation.name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredDesignations(filtered);
      setShowAutocomplete(true);
    } else {
      setFilteredDesignations(designations);
      setShowAutocomplete(false);
    }
  };

  const handleDesignationSelect = (designation: string, onChange: Function) => {
    onChange(designation);
    setDesignationSearchValue(designation);
    setShowAutocomplete(false);
    setShowDesignationDropdown(false); // Also close example dropdown
  };

  const handleDesignationFocus = () => {
    if (designations.length > 0) {
      setFilteredDesignations(designations);
      setShowAutocomplete(true);
      setShowDesignationDropdown(false); // Close example dropdown when focusing input
    }
  };

  const handleDesignationBlur = () => {
    // Delay hiding to allow for option clicks
    setTimeout(() => {
      setShowAutocomplete(false);
    }, 200);
  };

  const loadRoles = () => {
    callGetRoles(
      {},
      (message: string, data: any) => {
        console.log("Roles loaded successfully:", message);
        if (data?.roles) {
          setRoles(data.roles);
        }
      },
      (error: string) => {
        console.error("Error loading roles:", error);
        toast.error(t("members.permissions.messages.loadRolesError"));
      }
    );
  };

  const loadMemberData = (id: string) => {
    usersManagementApi.callGetUser(
      id,
      (message: string, responseData: any) => {
        console.log("Raw API response:", responseData);

        // Extract member data - it could be in responseData.member or directly in responseData
        const userData = responseData?.member || responseData;
        console.log("Extracted user data:", userData);

        if (userData) {
          // Map API response fields to form fields
          const fieldMapping = {
            name: userData.name,
            email: userData.email,
            designation: userData.designation,
            gender: userData.gender,
            employeeCode: userData.employee_code,
            homeTown: userData.home_town,
            language: userData.language,
            maritalStatus: userData.marital_status,
            nationality: userData.nationality,
            religion: userData.religion,
            permanentAddress: userData.permanent_address,
            currentAddress: userData.current_address,
            assignWorkspace: userData.assign_workspace,
          };

          console.log("Field mapping:", fieldMapping);

          // Set form values with proper mapping
          Object.entries(fieldMapping).forEach(([formField, apiValue]) => {
            if (apiValue !== undefined && apiValue !== null) {
              console.log(`Setting ${formField} to:`, apiValue);
              setValue(formField as keyof AddMemberFormData, apiValue);
            }
          });

          // Handle mobile number with country code
          if (userData.contact_number || userData.contact_no) {
            const contactNumber = userData.contact_number || userData.contact_no;
            const userPhoneCode = userData.phone_code || "+1";

            // Combine country code + mobile for PhoneInput display
            const phoneDigitsOnly = contactNumber.toString().replace(/\D/g, "");
            const countryCodeDigits = userPhoneCode.replace(/\D/g, "");

            // Create full phone number for PhoneInput
            let fullPhoneNumber = phoneDigitsOnly;
            if (!phoneDigitsOnly.startsWith(countryCodeDigits)) {
              fullPhoneNumber = countryCodeDigits + phoneDigitsOnly;
            }

            console.log(`Setting mobileNumber from ${contactNumber} to:`, fullPhoneNumber);
            setValue("mobileNumber", fullPhoneNumber);
            setValue("countryCode", userPhoneCode);
            setPhoneCode(userPhoneCode); // Also set the state variable
          }

          // Handle date of birth conversion from ISO string to Date
          if (userData.date_of_birth) {
            console.log("Setting dateOfBirth to:", new Date(userData.date_of_birth));
            setValue("dateOfBirth", new Date(userData.date_of_birth));
          }

          // Set profile photo URL if exists
          if (userData.profile_photo_url) {
            console.log("Setting profile photo URL to:", userData.profile_photo_url);
            setProfilePhotoUrl(userData.profile_photo_url);
          }
        }
      },
      (error: any) => {
        console.error("Failed to load member data:", error);
        toast.error(error || "Failed to load member data");
      }
    );
  };

  const onSubmit = (data: AddMemberFormData) => {
    console.log("Form submitted:", data);

    // Extract mobile number by removing country code prefix - use the country code from PhoneInput
    const fullPhoneNumber = data.mobileNumber.toString();
    const countryCode = data.countryCode || phoneCode || "";

    console.log("AddMember - Full Phone:", fullPhoneNumber);
    console.log("AddMember - Country Code (from form):", data.countryCode);
    console.log("AddMember - Country Code (from state):", phoneCode);
    console.log("AddMember - Country Code (final):", countryCode);

    // PhoneInput provides the full number with country code (e.g., "11212115151")
    // We need to extract just the mobile part by removing the country code digits
    const phoneDigitsOnly = fullPhoneNumber.replace(/\D/g, ""); // Remove non-digits
    const countryCodeDigits = countryCode.replace(/\D/g, ""); // Remove + sign

    let mobile = phoneDigitsOnly;
    if (countryCodeDigits && phoneDigitsOnly.startsWith(countryCodeDigits)) {
      mobile = phoneDigitsOnly.substring(countryCodeDigits.length);
    }

    console.log("AddMember - Mobile (extracted):", mobile);
    console.log("AddMember - Country Code (final):", countryCode);

    // Map form data to API parameters
    const apiData: any = {
      name: data.name,
      email: data.email,
      contact_no: mobile,
      contact_number: mobile,
      phone_code: countryCode, // Add phone_code to API payload
      designation: data.designation,
      gender: data.gender,
      date_of_birth: data.dateOfBirth ? moment(data.dateOfBirth).format("YYYY-MM-DD") : "",
      employee_code: data.employeeCode,
      home_town: data.homeTown,
      language: data.language,
      marital_status: data.maritalStatus,
      nationality: data.nationality,
      religion: data.religion,
      permanent_address: data.permanentAddress,
      current_address: data.currentAddress,
      assign_workspace: data.assignWorkspace,
      role_id: data.assignRole,
    };

    // Only include password fields if they have values or if it's create mode
    if (!isEditMode || (data.password && data.password.trim() !== "")) {
      apiData.password = data.password || "";
      apiData.confirm_password = data.confirmPassword || data.password || "";
    }

    console.log("Phone parsing:", {
      original: data.mobileNumber,
      fullPhoneNumber,
      phoneDigitsOnly,
      countryCode,
      mobile,
    });
    console.log("API Data to be sent:", apiData);

    if (isEditMode && memberId) {
      // Handle member update
      usersManagementApi.callUpdateUser(
        memberId,
        apiData,
        (message: string, response: any) => {
          toast.success(message || t("members.addMember.messages.memberUpdated"));
          // Navigate back to members list page
          history.push(URLS.MEMBERS.LIST);
        },
        (error: any) => {
          console.error("Failed to update member:", error);
          toast.error(error || t("members.addMember.messages.updateError"));
        }
      );
    } else {
      // Handle member creation
      console.log("Calling callCreateUser with data:", apiData);
      usersManagementApi.callCreateUser(
        apiData,
        (message: string, response: any) => {
          console.log("Member created successfully:", response);
          toast.success(message || t("members.addMember.messages.memberCreated"));
          // Navigate to members list page
          if (response && response.user_id) {
            history.push(URLS.MEMBERS.EDIT(response.user_id));
          } else {
            history.push(URLS.MEMBERS.LIST);
          }
        },
        (error: any) => {
          console.error("Failed to create member:", error);
          toast.error(error || t("members.addMember.messages.createError"));
        }
      );
    }
  };

  const uploadProfilePhotoToServer = async (file: File) => {
    if (!memberId) {
      toast.error(t("members.addMember.messages.memberIdRequired"));
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev < 90) return prev + 10;
          return prev;
        });
      }, 100);

      const formData = new FormData();
      formData.append("profile_photo", file);

      // Use direct fetch call like company logo upload
      const token = localStorage.getItem(STORAGE);
      const response = await fetch(`${API_URL.USERS_MANAGEMENT.UPLOAD_PROFILE_PHOTO}${memberId}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.ok) {
        const result = await response.json();
        console.log("Profile photo upload success:", result);

        // Update the profile photo URL
        if (result?.profile_photo_url || result?.data?.profile_photo_url) {
          setProfilePhotoUrl(result.profile_photo_url || result.data.profile_photo_url);
        }

        // Set uploaded file info
        setProfilePhoto(file);
        toast.success(result.message || t("members.addMember.messages.photoUploaded"));
      } else {
        const errorResult = await response.json();
        throw new Error(errorResult.message || `Upload failed: ${response.statusText}`);
      }
    } catch (error: any) {
      setUploadProgress(0);
      console.error("Profile photo upload error:", error);
      toast.error(error.message || t("members.addMember.messages.photoUploadError"));
    } finally {
      setIsUploading(false);
    }
  };
  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && isEditMode) {
      // Check file type
      if (!file.type.startsWith("image/")) {
        toast.error(t("members.addMember.messages.invalidFileType"));
        return;
      }

      // Check file size (max 25MB)
      const maxSize = 25 * 1024 * 1024; // 25MB in bytes
      if (file.size > maxSize) {
        toast.error(t("members.addMember.messages.fileSizeExceeds"));
        return;
      }

      uploadProfilePhotoToServer(file);
    }
  };

  const removePhoto = () => {
    setProfilePhoto(null);
    setProfilePhotoUrl(null);
    setUploadProgress(0);
  };

  const handleInviteSuccess = () => {
    setInviteModalOpen(false);
  };

  const handleInviteClose = () => {
    setInviteModalOpen(false);
  };

  return (
    <div className="dashboard-main add-member-page-wrapper">
      {/* Header */}
      <div className="drjob-page-header">
        <div className="drjob-header-left">
          <button className="back-button" onClick={() => history.push(URLS.MEMBERS.LIST)}>
            {t("members.addMember.backToList")}
          </button>
          <h1 className="drjob-page-title">
            {isEditMode ? t("members.addMember.editTitle") : t("members.addMember.title")}
          </h1>
        </div>
        <div className="drjob-header-right d-none">
          <button onClick={() => setInviteModalOpen(true)} className="btn-cancel">
            {t("members.actions.inviteMember")}
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="drjob-tabs">
        <button className={`drjob-tab ${activeTab === "info" ? "active" : ""}`} onClick={() => setActiveTab("info")}>
          {t("members.addMember.tabs.memberInfo")}
        </button>
        {memberId && (
          <button
            className={`drjob-tab ${activeTab === "permission" ? "active" : ""}`}
            onClick={() => setActiveTab("permission")}
          >
            {t("members.addMember.tabs.permission")}
          </button>
        )}
      </div>

      {/* Tab Content */}
      {activeTab === "info" && (
        <form onSubmit={handleSubmit(onSubmit)} className="drjob-member-form">
          {/* Upload Profile Photo Section - Only show in edit mode */}
          {isEditMode && (
            <div className="drjob-form-section">
              <h3>{t("members.addMember.profilePhoto.title")}</h3>
              <div className="drjob-photo-upload">
                {!profilePhoto && !profilePhotoUrl ? (
                  <div className="drjob-upload-area">
                    <div className="drjob-upload-icon">
                      <i className="fa fa-upload"></i>
                    </div>
                    <div className="drjob-upload-text">
                      <span className="drjob-upload-link">{t("members.addMember.profilePhoto.clickToUpload")}</span>{" "}
                      {t("members.addMember.profilePhoto.dragAndDrop")}
                    </div>
                    <div className="drjob-upload-hint">{t("members.addMember.profilePhoto.maxFileSize")}</div>
                    <div className="drjob-upload-formats">{t("members.addMember.profilePhoto.supportedFormats")}</div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="drjob-file-input"
                      disabled={isUploading}
                    />
                  </div>
                ) : profilePhotoUrl ? (
                  <div className="drjob-upload-preview">
                    <div className="drjob-photo-preview">
                      <img
                        src={profilePhotoUrl}
                        alt="Profile Preview"
                        style={{
                          maxWidth: "200px",
                          maxHeight: "200px",
                          objectFit: "cover",
                          borderRadius: "8px",
                        }}
                      />
                    </div>
                    <div className="drjob-file-info">
                      <i className="fa fa-file-image-o"></i>
                      <span>{profilePhoto?.name || "Profile Photo"}</span>
                      <span>{profilePhoto ? `${Math.round(profilePhoto.size / 1024)} KB` : ""}</span>
                      <button type="button" onClick={removePhoto} className="drjob-remove-file">
                        <i className="fa fa-trash"></i>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="drjob-upload-progress">
                    <div className="drjob-file-info">
                      <i className="fa fa-file-image-o"></i>
                      <span>{profilePhoto?.name || t("members.addMember.profilePhoto.profilePhoto")}</span>
                      <span>{profilePhoto ? `${Math.round(profilePhoto.size / 1024)} KB` : ""}</span>
                      <button type="button" onClick={removePhoto} className="drjob-remove-file">
                        <i className="fa fa-trash"></i>
                      </button>
                    </div>
                    {(isUploading || uploadProgress > 0) && (
                      <div className="drjob-progress-bar">
                        <div className="drjob-progress-fill" style={{ width: `${uploadProgress}%` }}></div>
                      </div>
                    )}
                    {isUploading && <div className="drjob-progress-text">{uploadProgress}%</div>}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Form Fields */}
          <div className="drjob-form-grid">
            <div className="drjob-form-row">
              <div className="drjob-form-group">
                <label className="form-label" htmlFor="name">
                  {t("members.addMember.form.name")} <span className="mandatory-mark">*</span>
                </label>
                <Controller
                  control={control}
                  name="name"
                  rules={{ required: t("members.addMember.validation.nameRequired") }}
                  render={({ onChange, onBlur, value, name, ref }) => (
                    <input
                      type="text"
                      id="name"
                      placeholder={t("members.addMember.placeholders.enterName")}
                      className={`form-control ${errors.name ? "is-invalid" : ""}`}
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value}
                      ref={ref}
                      name={name}
                    />
                  )}
                />
                {errors.name && <div className="invalid-feedback">{errors.name.message}</div>}
              </div>

              <div className="drjob-form-group">
                <label className="form-label" htmlFor="mobile">
                  {t("members.addMember.form.mobile")} <span className="mandatory-mark">*</span>
                </label>
                <div className="figma-phone-wrapper">
                  <Controller
                    control={control}
                    name="mobileNumber"
                    rules={{ required: t("members.addMember.validation.mobileRequired") }}
                    render={({ onChange, onBlur, value }) => (
                      <PhoneInput
                        country={defaultCountry}
                        value={value || ""}
                        onChange={(phone: string, country: any) => {
                          console.log("AddMember PhoneInput onChange:", phone, "dialCode:", country.dialCode);
                          onChange(phone);
                          const code = `+${country.dialCode}`;
                          setValue("countryCode", code);
                          setPhoneCode(code);
                          console.log("AddMember phone_code set to:", code);
                        }}
                        onBlur={onBlur}
                        placeholder={t("members.addMember.placeholders.enterMobile")}
                        inputClass={`figma-phone-input ${errors.mobileNumber ? "error" : ""}`}
                        containerClass="phone-input-container"
                      />
                    )}
                  />
                </div>
                {errors.mobileNumber && <div className="invalid-feedback">{errors.mobileNumber.message}</div>}
              </div>
            </div>

            <div className="drjob-form-row">
              <div className="drjob-form-group">
                <label className="form-label" htmlFor="email">
                  {t("members.addMember.form.email")} <span className="mandatory-mark">*</span>
                </label>
                <Controller
                  control={control}
                  name="email"
                  rules={{
                    required: t("members.addMember.validation.emailRequired"),
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: t("members.addMember.validation.invalidEmail"),
                    },
                  }}
                  render={({ onChange, onBlur, value, name, ref }) => (
                    <input
                      type="email"
                      id="email"
                      placeholder={t("members.addMember.placeholders.enterEmail")}
                      className={`form-control ${errors.email ? "is-invalid" : ""}`}
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value}
                      ref={ref}
                      name={name}
                    />
                  )}
                />
                {errors.email && <div className="invalid-feedback">{errors.email.message}</div>}
              </div>

              <div className="drjob-form-group designation-autocomplete">
                <label className="form-label" htmlFor="designation">
                  {t("members.addMember.form.designation")} <span className="mandatory-mark">*</span>
                </label>
                <Controller
                  control={control}
                  name="designation"
                  rules={{ required: t("members.addMember.validation.designationRequired") }}
                  render={({ onChange, value }) => (
                    <div className="figma-autocomplete-container" ref={designationRef}>
                      <div
                        className={`figma-autocomplete-wrapper ${errors.designation ? "error" : ""}`}
                        ref={designationInputRef}
                      >
                        <ReactSearchAutocomplete
                          items={designations}
                          onSearch={handleDesignationSearch}
                          onSelect={(item) => {
                            onChange(item.name);
                            setValue("designation", item.name);
                            // Close the dropdown by blurring the input
                            setTimeout(() => {
                              if (designationInputRef.current) {
                                const input = designationInputRef.current.querySelector("input");
                                if (input) {
                                  input.blur();
                                }
                              }
                            }, 100);
                          }}
                          autoFocus={false}
                          resultStringKeyName="name"
                          inputSearchString={value || ""}
                          formatResult={(item: any) => (
                            <div className="autocomplete-result">
                              <span>{item.name}</span>
                            </div>
                          )}
                          placeholder={t("members.addMember.placeholders.searchDesignation")}
                          styling={{
                            border: "1px solid #D7D7D7",
                            borderRadius: "6px",
                            backgroundColor: "#F6F6F6",
                            boxShadow: "none",
                            color: "#374151",
                            lineColor: "#e5e7eb",
                            placeholderColor: "#999",
                            zIndex: 2,
                          }}
                          fuseOptions={{ keys: ["name"] }}
                        />
                      </div>

                      {/* <button
                        type="button"
                        className="figma-example-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowDesignationDropdown(!showDesignationDropdown);
                        }}
                      >
                        {t("members.addMember.buttons.example")}
                      </button> */}
                      <span className="example-text">
                        <b>Example:</b> HR Manager
                      </span>

                      {showDesignationDropdown && (
                        <div className="designation-example-dropdown">
                          <div className="designation-options-grid">
                            {designations.map((designation) => (
                              <button
                                key={designation.id}
                                type="button"
                                className="designation-option-btn"
                                // onClick={(e) => {
                                //   e.preventDefault();
                                //   e.stopPropagation();
                                //   onChange(designation.name);
                                //   setValue("designation", designation.name);
                                //   setShowDesignationDropdown(false);
                                //   // Also blur the input to close any autocomplete
                                //   setTimeout(() => {
                                //     if (designationInputRef.current) {
                                //       const input = designationInputRef.current.querySelector("input");
                                //       if (input) {
                                //         input.blur();
                                //       }
                                //     }
                                //   }, 100);
                                // }}
                              >
                                {designation.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                />
                {errors.designation && <div className="invalid-feedback">{errors.designation.message}</div>}
              </div>
            </div>

            <div className="drjob-form-row">
              <div className="drjob-form-group">
                <label className="form-label">{t("members.addMember.form.gender")}</label>
                <div className="radio-group">
                  <Controller
                    control={control}
                    name="gender"
                    render={({ onChange, value }) => (
                      <div className="radio-options" style={{ flexDirection: "row" }}>
                        <label className="radio-option">
                          <input
                            type="radio"
                            value="female"
                            checked={value === "female"}
                            onChange={() => onChange("female")}
                          />
                          {t("members.addMember.options.gender.female")}
                        </label>
                        <label className="radio-option">
                          <input
                            type="radio"
                            value="male"
                            checked={value === "male"}
                            onChange={() => onChange("male")}
                          />
                          {t("members.addMember.options.gender.male")}
                        </label>
                        {/* <label className="radio-option">
                          <input
                            type="radio"
                            value="no-preference"
                            checked={value === "no-preference"}
                            onChange={() => onChange("no-preference")}
                          />
                          {t("members.addMember.options.gender.noPreference")}
                        </label> */}
                      </div>
                    )}
                  />
                </div>
              </div>

              <div className="drjob-form-group">
                <label className="form-label" htmlFor="dob">
                  {t("members.addMember.form.dateOfBirth")} <span className="mandatory-mark">*</span>
                </label>
                <Controller
                  control={control}
                  name="dateOfBirth"
                  rules={{ required: t("members.addMember.validation.dobRequired") }}
                  render={({ onChange, onBlur, value, name, ref }) => (
                    <DateInput
                      onChange={(e) => {
                        onChange(e);
                      }}
                      onBlur={onBlur}
                      value={value}
                      maxDate={new Date()}
                      dateFormat={DATE_FORMAT}
                      inputRef={ref}
                      // error={errors.dateOfBirth}
                      placeholder={t("members.addMember.form.dateOfBirth")}
                      id="dob"
                    />
                  )}
                />
                {errors.dateOfBirth && <div className="invalid-feedback">{errors.dateOfBirth.message}</div>}
              </div>
            </div>

            <div className="drjob-form-row">
              <div className="drjob-form-group">
                <label className="form-label" htmlFor="employeeCode">
                  {t("members.addMember.form.employeeCode")} <span className="mandatory-mark">*</span>
                </label>
                <Controller
                  control={control}
                  name="employeeCode"
                  rules={{ required: t("members.addMember.validation.employeeCodeRequired") }}
                  render={({ onChange, onBlur, value, name, ref }) => (
                    <input
                      type="text"
                      id="employeeCode"
                      placeholder={t("members.addMember.placeholders.enterCode")}
                      className={`form-control ${errors.employeeCode ? "is-invalid" : ""}`}
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value}
                      name={name}
                      ref={ref}
                    />
                  )}
                />
                {errors.employeeCode && <div className="invalid-feedback">{errors.employeeCode.message}</div>}
              </div>

              <div className="drjob-form-group">
                <label className="form-label" htmlFor="homeTown">
                  {t("members.addMember.form.homeTown")}
                </label>
                <Controller
                  control={control}
                  name="homeTown"
                  render={({ onChange, onBlur, value, name, ref }) => (
                    <input
                      type="text"
                      id="homeTown"
                      placeholder={t("members.addMember.placeholders.enterHomeTown")}
                      className="form-control"
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value}
                      name={name}
                      ref={ref}
                    />
                  )}
                />
              </div>
            </div>

            <div className="drjob-form-row">
              <div className="drjob-form-group">
                <label className="form-label" htmlFor="language">
                  {t("members.addMember.form.language")}
                </label>
                <Controller
                  control={control}
                  name="language"
                  render={({ onChange, onBlur, value, name, ref }) => (
                    <select
                      id="language"
                      className="basic-select"
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value}
                      ref={ref}
                      name={name}
                    >
                      <option value="">{t("members.addMember.placeholders.selectLanguage")}</option>
                      <option value="English">{t("members.addMember.options.languages.english")}</option>
                      <option value="Arabic">{t("members.addMember.options.languages.arabic")}</option>
                      <option value="Other">{t("members.addMember.options.languages.other")}</option>
                    </select>
                  )}
                />
              </div>

              <div className="drjob-form-group">
                <label className="form-label" htmlFor="maritalStatus">
                  {t("members.addMember.form.maritalStatus")}
                </label>
                <Controller
                  control={control}
                  name="maritalStatus"
                  render={({ onChange, onBlur, value, name, ref }) => (
                    <select
                      id="maritalStatus"
                      className="basic-select"
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value}
                      ref={ref}
                      name={name}
                    >
                      <option value="">{t("members.addMember.placeholders.selectStatus")}</option>
                      <option value="Single">{t("members.addMember.options.maritalStatus.single")}</option>
                      <option value="Married">{t("members.addMember.options.maritalStatus.married")}</option>
                      <option value="Divorced">{t("members.addMember.options.maritalStatus.divorced")}</option>
                      <option value="Other">{t("members.addMember.options.maritalStatus.other")}</option>
                    </select>
                  )}
                />
              </div>
            </div>

            <div className="drjob-form-row">
              <div className="drjob-form-group">
                <label className="form-label" htmlFor="nationality">
                  {t("members.addMember.form.nationality")}
                </label>
                <Controller
                  control={control}
                  name="nationality"
                  render={({ onChange, onBlur, value, name, ref }) => (
                    <select
                      id="nationality"
                      className="basic-select"
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value}
                      ref={ref}
                      name={name}
                    >
                      <option value="">{t("members.addMember.placeholders.selectNationality")}</option>
                      <option value="American">{t("members.addMember.options.nationalities.american")}</option>
                      <option value="British">{t("members.addMember.options.nationalities.british")}</option>
                      <option value="Canadian">{t("members.addMember.options.nationalities.canadian")}</option>
                      <option value="Australian">{t("members.addMember.options.nationalities.australian")}</option>
                      <option value="Indian">{t("members.addMember.options.nationalities.indian")}</option>
                      <option value="Chinese">{t("members.addMember.options.nationalities.chinese")}</option>
                      <option value="Mexican">{t("members.addMember.options.nationalities.mexican")}</option>
                      <option value="Other">{t("members.addMember.options.nationalities.other")}</option>
                    </select>
                  )}
                />
              </div>

              <div className="drjob-form-group">
                <label className="form-label" htmlFor="religion">
                  {t("members.addMember.form.religion")}
                </label>
                <Controller
                  control={control}
                  name="religion"
                  render={({ onChange, onBlur, value, name, ref }) => (
                    <select
                      id="religion"
                      className="basic-select"
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value}
                      ref={ref}
                      name={name}
                    >
                      <option value="">{t("members.addMember.placeholders.selectReligion")}</option>
                      <option value="Christianity">{t("members.addMember.options.religions.christianity")}</option>
                      <option value="Hinduism">{t("members.addMember.options.religions.hinduism")}</option>
                      <option value="Islam">{t("members.addMember.options.religions.islam")}</option>
                      <option value="Judaism">{t("members.addMember.options.religions.judaism")}</option>
                      <option value="Buddhism">{t("members.addMember.options.religions.buddhism")}</option>
                      <option value="Other">{t("members.addMember.options.religions.other")}</option>
                    </select>
                  )}
                />
              </div>
            </div>

            <div className="drjob-form-group full-width">
              <label className="form-label" htmlFor="permanentAddress">
                {t("members.addMember.form.permanentAddress")}
              </label>
              <Controller
                control={control}
                name="permanentAddress"
                render={({ onChange, onBlur, value, name, ref }) => (
                  <textarea
                    id="permanentAddress"
                    placeholder={t("members.addMember.placeholders.enterAddress")}
                    className="form-control"
                    rows={4}
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value}
                    ref={ref}
                    name={name}
                  />
                )}
              />
            </div>

            <div className="drjob-form-group full-width">
              <label className="form-label" htmlFor="currentAddress">
                {t("members.addMember.form.currentAddress")}
              </label>
              <Controller
                control={control}
                name="currentAddress"
                render={({ onChange, onBlur, value, name, ref }) => (
                  <textarea
                    id="currentAddress"
                    placeholder={t("members.addMember.placeholders.enterAddress")}
                    className="form-control"
                    rows={4}
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value}
                    ref={ref}
                    name={name}
                  />
                )}
              />
            </div>

            <div className="drjob-form-row">
              <div className="drjob-form-group">
                <label className="form-label" htmlFor="password">
                  {t("members.addMember.form.password")} {!isEditMode && <span className="mandatory-mark">*</span>}
                </label>
                <Controller
                  control={control}
                  name="password"
                  rules={isEditMode ? {} : { required: t("members.addMember.validation.passwordRequired") }}
                  render={({ onChange, onBlur, value, name, ref }) => (
                    <input
                      type="password"
                      id="password"
                      placeholder={t("members.addMember.placeholders.enterPassword")}
                      className={`form-control ${errors.password ? "is-invalid" : ""}`}
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value}
                      ref={ref}
                      name={name}
                    />
                  )}
                />
                {errors.password && <div className="invalid-feedback">{errors.password.message}</div>}
              </div>

              <div className="drjob-form-group">
                <label className="form-label" htmlFor="confirmPassword">
                  {t("members.addMember.form.confirmPassword")}{" "}
                  {!isEditMode && <span className="mandatory-mark">*</span>}
                </label>
                <Controller
                  control={control}
                  name="confirmPassword"
                  rules={
                    isEditMode
                      ? {
                          validate: (value) => {
                            const password = watch("password");
                            return value === password || t("members.addMember.validation.passwordsNotMatch");
                          },
                        }
                      : {
                          required: t("members.addMember.validation.confirmPasswordRequired"),
                          validate: (value) => {
                            const password = watch("password");
                            return value === password || t("members.addMember.validation.passwordsNotMatch");
                          },
                        }
                  }
                  render={({ onChange, onBlur, value, name, ref }) => (
                    <input
                      type="password"
                      id="confirmPassword"
                      placeholder={t("members.addMember.placeholders.enterConfirmPassword")}
                      className={`form-control ${errors.confirmPassword ? "is-invalid" : ""}`}
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value}
                      ref={ref}
                      name={name}
                    />
                  )}
                />
                {errors.confirmPassword && <div className="invalid-feedback">{errors.confirmPassword.message}</div>}
              </div>
            </div>
            <div className="drjob-form-row">
              <div className="drjob-form-group">
                <label className="form-label" htmlFor="assignWorkspace">
                  {t("members.addMember.form.assignWorkspace")}
                </label>
                <Controller
                  control={control}
                  name="assignWorkspace"
                  render={({ onChange, onBlur, value, name, ref }) => (
                    <select
                      id="assignWorkspace"
                      className="basic-select"
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value}
                      ref={ref}
                      name={name}
                    >
                      <option value="">{t("members.addMember.placeholders.selectWorkspace")}</option>
                      {workplacesData.map((workplace: any) => (
                        <option key={workplace.id || workplace.value} value={workplace.name || workplace.label}>
                          {workplace.name || workplace.label}
                        </option>
                      ))}
                    </select>
                  )}
                />
              </div>
              {!isEditMode && (
                <div className="drjob-form-group">
                  <label className="form-label" htmlFor="assignRole">
                    {t("members.addMember.form.assignRole")}
                  </label>
                  <Controller
                    control={control}
                    name="assignRole"
                    render={({ onChange, onBlur, value, name, ref }) => (
                      <select
                        id="assignRole"
                        className="basic-select"
                        onChange={(e) => {
                          const roleId = parseInt(e.target.value);
                          const role = roles.find((r) => r.id === roleId);
                          onChange(roleId);
                          // You can add additional logic here if needed
                        }}
                        onBlur={onBlur}
                        value={value || (roles.length > 0 ? roles[0].id : 0)}
                        ref={ref}
                        name={name}
                        disabled={roles.length === 0}
                      >
                        {roles.map((role) => (
                          <option key={role.id} value={role.id}>
                            {role.name}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Form Actions */}
          <div className="drjob-form-actions">
            <button onClick={() => history.push(URLS.MEMBERS.LIST)} className="btn-cancel">
              {t("members.addMember.buttons.cancel")}
            </button>
            <button type="submit" className="btn-preview">
              {isEditMode ? t("members.addMember.buttons.update") : t("members.addMember.buttons.save")}
            </button>
          </div>
        </form>
      )}

      {activeTab === "permission" && memberId && (
        <div className="drjob-permission-tab">
          <PermissionList />
        </div>
      )}

      {/* Invite Member Modal */}
      <InviteMemberModal isOpen={inviteModalOpen} onClose={handleInviteClose} onSuccess={handleInviteSuccess} />
    </div>
  );
};

export default AddMemberForm;
